import * as PowerBI from 'powerbi-client';
import React, { Suspense, useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Paper, Toolbar, Button } from '@mui/material';
import './powerbi.css';
import Auth from '../../auth';

const EmbeddedThoughtSpotLiveboard = React.lazy(() =>
    import('./components/EmbeddedThoughtSpotLiveboard')
);

const pbi = new PowerBI.service.Service(
  PowerBI.factories.hpmFactory,
  PowerBI.factories.wpmpFactory,
  PowerBI.factories.routerFactory)

type EmbeddedReportConfig = {
    ReportId?: string | number;
    EmbedUrl?: string;
    EmbedToken?: string;
    Expiration?: string;
    Editable?: boolean;
    // viewMode/permissions are added and `delete`d dynamically by toggleEdit,
    // and embedUrl is set (lowercase) once state mirrors the embed config.
    [key: string]: any;
};

export const hasReport = (report?: EmbeddedReportConfig | null) => !!(report && report.EmbedUrl && report.EmbedToken);

// LB-505: default a Liveboard-only user (Spotter enabled, no real PowerBI report) straight to
// the ThoughtSpot view; everyone else starts on the classic PowerBI view.
const getInitialView = (props?: AdvancedReportsProps): ActiveView =>
    props && props.thoughtSpotEnabled && !hasReport(props.embeddedReport) ? 'thoughtspot' : 'powerbi';

const reportingSwitchButtonSx = {
    height: '38px',
    minWidth: '190px',
    color: 'white',
    borderColor: 'rgba(255, 255, 255, 0.75)',
    textTransform: 'none',
    whiteSpace: 'nowrap',
    mr: 1,
    '&:hover': {
        backgroundColor: 'rgba(255, 255, 255, 0.12)',
        borderColor: 'white',
    },
};

const reportsDiffer = (prevReport?: EmbeddedReportConfig | null, nextReport?: EmbeddedReportConfig | null) => {
    if (!prevReport || !nextReport) {
        return prevReport !== nextReport;
    }
    return prevReport.EmbedUrl !== nextReport.EmbedUrl ||
        prevReport.EmbedToken !== nextReport.EmbedToken ||
        prevReport.ReportId !== nextReport.ReportId ||
        prevReport.Expiration !== nextReport.Expiration;
};

type ActiveView = 'powerbi' | 'thoughtspot';

type AdvancedReportsProps = {
    setTitle?: (title: string) => void;
    companyName?: string;
    embeddedReport?: EmbeddedReportConfig | null;
    thoughtSpotEnabled?: boolean;
    thoughtSpotEmbed?: { EmbedToken?: string | null } | null;
    // Rendered via <Route render={props => <AdvancedReports {...props} .../>}>,
    // which spreads RouteComponentProps (history/location/match). Absorb them.
    [key: string]: any;
};

type AdvancedReportsState = {
    embeddedReport: EmbeddedReportConfig;
    activeView: ActiveView;
};

const AdvancedReports = (props: AdvancedReportsProps) => {
    const {setTitle, embeddedReport: reportProp, thoughtSpotEnabled, thoughtSpotEmbed} = props;

    const [embeddedReport, setEmbeddedReport] = useState<AdvancedReportsState['embeddedReport']>({});
    const [activeView, setActiveView] = useState<ActiveView>(() => getInitialView(props));

    const resetEmbed = useCallback(() => {
        let element = document.getElementById("embeddedReport");
        if(element){
            pbi.reset(element);
        }
    }, []);

    const updateReport = useCallback((config?: EmbeddedReportConfig | null) => {
        if(!config){
            resetEmbed();
            return;
        }
        let element = document.getElementById("embeddedReport");
        if(!element){
            return;
        }
        let embedConfig: EmbeddedReportConfig = {
        	type: 'report',
        	id: config.ReportId,
        	embedUrl: config.EmbedUrl,
        	tokenType: PowerBI.models.TokenType.Embed,
        	accessToken: config.EmbedToken,
            settings: {
                panes: {
                    filters: {
                        expanded: false
                    }
                }
            }
        };
        setEmbeddedReport(embedConfig);
        let report: any = pbi.embed(element, embedConfig as any);
        try{
            report.on('pageChanged', (e: any) => {(window as any).pendo.track('powerbi_page', {page: e.detail.newPage.displayName, reportId: e.detail.newPage.report.config.id})})
        }
        catch {}
    }, [resetEmbed]);

    // Mirror componentDidMount + componentDidUpdate: the embed is imperative and
    // reads the #embeddedReport element, so it must run after render. Refs hold
    // the previous prop/view for the prev-vs-next comparisons the class did.
    const didMountRef = useRef(false);
    const prevReportRef = useRef<EmbeddedReportConfig | null | undefined>(undefined);
    const prevActiveViewRef = useRef<ActiveView>(activeView);
    // Mirrors componentDidUpdate: runs every render with manual prev-vs-next guards,
    // so an exhaustive dep list would defeat the intent (and the guards prevent loops).
    // eslint-disable-next-line react-hooks/exhaustive-deps
    useLayoutEffect(() => {
        if(!didMountRef.current){
            // componentDidMount
            didMountRef.current = true;
            prevReportRef.current = reportProp;
            prevActiveViewRef.current = activeView;
            if(activeView === 'powerbi'){
                updateReport(reportProp);
            }
            return;
        }
        // componentDidUpdate
        const prevReport = prevReportRef.current;
        const prevActiveView = prevActiveViewRef.current;
        prevReportRef.current = reportProp;
        prevActiveViewRef.current = activeView;

        if(activeView !== 'powerbi' && !thoughtSpotEnabled && hasReport(reportProp)){
            // LB-503/506: only fall back to PowerBI when Liveboard has been turned off AND a
            // real PowerBI report exists. A Liveboard-only user (no PowerBI report) must stay on
            // the ThoughtSpot view rather than be forced into an empty PowerBI container.
            setActiveView('powerbi');
            return;
        }
        if(activeView !== 'powerbi'){
            return;
        }
        if(prevActiveView !== 'powerbi'){
            // Re-embed PowerBI when switching back; the container was unmounted.
            updateReport(reportProp);
            return;
        }
        if(reportsDiffer(prevReport, reportProp)){
            updateReport(reportProp);
        } else if (!hasReport(reportProp) && embeddedReport.embedUrl){
            updateReport(null);
        }
    });

    // componentWillUnmount
    useLayoutEffect(() => {
        return () => {
            resetEmbed();
        };
    }, [resetEmbed]);

    // Keep the page title in sync with the active view (master #79 rebrand:
    // PowerBI view = 'Advanced Reporting', ThoughtSpot view = 'Intelligent Reporting').
    // Covers both the mount title and the on-switch update the class did in setState.
    useEffect(() => {
        if(setTitle){
            setTitle(activeView === 'powerbi' ? 'Advanced Reporting' : 'Intelligent Reporting');
        }
    }, [activeView, setTitle]);

    const toggleEdit = () => {
        var edit = embeddedReport.viewMode !== PowerBI.models.ViewMode.Edit;
        var config: EmbeddedReportConfig = {...embeddedReport};
        if(edit){
            config.viewMode = PowerBI.models.ViewMode.Edit;
            config.permissions = PowerBI.models.Permissions.All;
        } else {
            delete config.viewMode;
            delete config.permissions;
        }
        pbi.embed(document.getElementById("embeddedReport") as HTMLElement, config as any);
        setEmbeddedReport(config);
    };

    const switchReportingView = () => {
        const nextView: ActiveView = activeView === 'powerbi' ? 'thoughtspot' : 'powerbi';
        if(nextView !== 'powerbi'){
            // Tear down the PowerBI embed before swapping in the ThoughtSpot view
            // so the two SDKs do not conflict over the container.
            resetEmbed();
        }
        // Re-embed on switch back to PowerBI is handled by the lifecycle effect's
        // view-transition branch once the container remounts.
        setActiveView(nextView);
    };

    let canManageSettings = Auth.hasPermission('settings/manage');
    let editable = (reportProp || {}).Editable && canManageSettings;
    let thoughtSpotAuthToken = (thoughtSpotEmbed || {}).EmbedToken;
    let isPowerBI = activeView === 'powerbi';
    let showViewToggle = thoughtSpotEnabled && hasReport(reportProp);
    let reportingSwitchLabel = isPowerBI ? 'Try Intelligent Reporting' : 'Advanced Reporting';
    let reportingHeader = isPowerBI ? 'Advanced Reporting' : 'Intelligent Reporting';
    return <Paper sx={{ overflow: 'hidden' }}>
        <Toolbar className='lbtoolbar'>
            <span style={{flex: 1, textAlign: 'left'}}>{reportingHeader}</span>
            {showViewToggle && <Button
                variant='outlined'
                size='small'
                sx={reportingSwitchButtonSx}
                onClick={switchReportingView}
                aria-label={reportingSwitchLabel}>
                {reportingSwitchLabel}
            </Button>}
            {isPowerBI && editable && <Button onClick={toggleEdit} style={{color: 'white'}}>{embeddedReport.viewMode ? 'Stop editing' : 'Edit'}</Button>}
        </Toolbar>
        {isPowerBI
            ? <div id="embeddedReport"></div>
            : <Suspense fallback={null}>
                <EmbeddedThoughtSpotLiveboard authToken={thoughtSpotAuthToken} hasPowerBI={hasReport(reportProp)} />
            </Suspense>}
    </Paper>;
};

export default AdvancedReports;
